import { CheckCircle2, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent
} from "react";
import { openEntityRoute } from "../../app/entityRoutes.js";
import type { ToastMessage } from "../context.js";
import { useTranslation } from "react-i18next";

// --- Timing (#586) -----------------------------------------------------------
// The toast used to be a flat 4s / 8s. Long assistant confirmations ("Added
// SUNGOLD KIWI (1 each) → BJs Shopping list") could not be read in 4s, and a
// marquee makes that worse because the tail arrives late. So the dwell scales
// with how much there is to read, floors at the old values, and hard-caps so a
// toast can never camp on screen.
const BASE_MS = 4000;
const PER_CHAR_MS = 45;
const ACTION_FLOOR_MS = 8000;
const MAX_MS = 15000;
// Settle time after the marquee returns home, so the pill does not vanish on
// the same frame the text lands.
const SETTLE_MS = 700;

// The "brief delay" before scrolling: long enough to read the head of the line
// in place, short enough that it does not feel stuck.
const MARQUEE_DELAY_MS = 900;
const MARQUEE_SPEED_PX_PER_SEC = 60;
// Share of one there-and-back cycle spent moving (the rest is the hold at each
// end). Must match the keyframe percentages in apps/web/src/styles/.
const MARQUEE_TRAVEL_FRACTION = 0.38;
const MARQUEE_MIN_CYCLE_MS = 2400;

// Breathing room handed back when an over-long hover/focus is released, so the
// toast never vanishes on the same frame the pointer leaves.
const RESUME_GRACE_MS = 900;

// Swipe past this many pixels and the toast goes; below it, it springs back.
const SWIPE_DISMISS_PX = 72;
// Slop before a drag counts as a swipe, so a tap on the pill is not a flick.
const SWIPE_START_PX = 8;
const SWIPE_FADE_PX = 220;

/** Auto-dismiss dwell for a toast, scaled to reading length and capped. */
export function toastDurationMs(message: string, hasActions: boolean): number {
  const scaled = BASE_MS + message.length * PER_CHAR_MS;
  return Math.min(MAX_MS, Math.max(hasActions ? ACTION_FLOOR_MS : BASE_MS, scaled));
}

/**
 * Marquee cycle for `overflowPx` of hidden text. The ideal cycle scrolls at a
 * readable speed, but it is squeezed to fit the dwell cap: showing the whole
 * line a little fast beats dismissing halfway through it, which is the actual
 * ask ("make it scroll so the line gets displayed").
 */
export function marqueeCycleMs(overflowPx: number): number {
  const travelMs = (Math.max(0, overflowPx) / MARQUEE_SPEED_PX_PER_SEC) * 1000;
  const ideal = travelMs / MARQUEE_TRAVEL_FRACTION;
  const budget = MAX_MS - MARQUEE_DELAY_MS - SETTLE_MS;
  return Math.round(Math.max(MARQUEE_MIN_CYCLE_MS, Math.min(ideal, budget)));
}

/** Dwell that also guarantees one full marquee round trip, still under the cap. */
export function toastDurationWithMarqueeMs(message: string, hasActions: boolean, overflowPx: number): number {
  const base = toastDurationMs(message, hasActions);
  if (overflowPx <= 0) return base;
  const needed = MARQUEE_DELAY_MS + marqueeCycleMs(overflowPx) + SETTLE_MS;
  return Math.min(MAX_MS, Math.max(base, needed));
}

/**
 * A link to where you already are is not an affordance, it is furniture — and
 * plenty of toasts fire on the very page their entity lives on ("added to your
 * Food Catalog", raised from the Food Catalog). Callers attach the link
 * unconditionally and this drops it when it would not move you.
 */
function usefulLink(link: ToastMessage["link"]): ToastMessage["link"] {
  if (!link) return undefined;
  if (typeof window === "undefined") return link;
  const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return here === link.path ? undefined : link;
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function AppToast({ toast, onDismiss }: { toast: ToastMessage; onDismiss: () => void }) {
  const { t } = useTranslation("shell");
  const pillRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLSpanElement | null>(null);
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [overflowPx, setOverflowPx] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(prefersReducedMotion);
  // Hover and focus are tracked apart, not as one `held` flag: with a single
  // flag, moving the pointer off the pill while a button inside it still has
  // focus (or blurring a child while the pointer is still on the pill) clears
  // the pause and the toast dismisses out from under the person using it.
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const [dragX, setDragX] = useState(0);
  // Resolved once: the toast outlives at most one navigation, and a link that
  // vanished mid-read because something else moved the route would be worse
  // than one that briefly points at the current page.
  const [link] = useState(() => usefulLink(toast.link));
  const dragRef = useRef<{ pointerId: number; startX: number; startY: number; active: boolean } | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduceMotion(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  /**
   * Hover, read off the POINTER'S POSITION rather than off the pill's own mouse
   * events (#1489).
   *
   * The pill is `pointer-events: none` on everything but its affordances, so it
   * no longer takes clicks aimed at the page beneath it — and an element that is
   * not hit-tested never receives `mouseenter` either. Tracking the pointer
   * against the pill's rect keeps the pause exactly where it was earned: reading
   * a long line, or a marquee somebody wants to stop mid-scroll.
   *
   * Two things fall out of this that are improvements rather than costs:
   *
   * - A pill that RISES UNDER a resting pointer no longer pauses itself. The
   *   browser re-hit-tests on layout and fires `mouseover` at the old position,
   *   which is how #1489's toast came up already `is-paused` — from the hand
   *   that had just pressed the button below it, with no hover intended. Only a
   *   pointer that MOVES onto the pill pauses it now, which is the "hover with
   *   intent" that issue asked for.
   * - Touch and pen are ignored. The compatibility `mouseenter` a tap fires used
   *   to pause a toast for as long as nothing else moved, which on a touchscreen
   *   is until the next tap.
   *
   * `mouseleave` on the document covers the pointer leaving the window while it
   * is over the pill: no further `pointermove` arrives to say it has gone.
   */
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const track = (event: PointerEvent) => {
      if (event.pointerType !== "mouse" && event.pointerType !== "pen") return;
      const rect = pillRef.current?.getBoundingClientRect();
      const inside = Boolean(rect)
        && event.clientX >= rect!.left && event.clientX <= rect!.right
        && event.clientY >= rect!.top && event.clientY <= rect!.bottom;
      // Returning the same value makes React bail out, so a mouse crossing the
      // window does not re-render the toast sixty times a second.
      setHovered((current) => (current === inside ? current : inside));
    };
    const left = () => setHovered((current) => (current ? false : current));
    window.addEventListener("pointermove", track, { passive: true });
    document.addEventListener("mouseleave", left);
    return () => {
      window.removeEventListener("pointermove", track);
      document.removeEventListener("mouseleave", left);
    };
  }, []);

  // How much of the line is hidden. Measured rather than estimated because the
  // pill's width depends on the viewport, the link, and the action buttons.
  const measure = useCallback(() => {
    const viewport = viewportRef.current;
    const text = textRef.current;
    if (!viewport || !text) return;
    setOverflowPx(Math.max(0, Math.round(text.scrollWidth - viewport.clientWidth)));
  }, []);

  // useEffect, not useLayoutEffect: the marquee does not start for ~900ms, so
  // measuring one paint late is invisible, and it keeps the component clean to
  // render through react-dom/server (which is how the suite tests it).
  useEffect(() => {
    if (reduceMotion) {
      setOverflowPx(0);
      return undefined;
    }
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    if (viewportRef.current) observer.observe(viewportRef.current);
    return () => observer.disconnect();
  }, [measure, reduceMotion, toast.id, toast.message]);

  const hasActions = Boolean(toast.actions?.length);
  const totalMs = toastDurationWithMarqueeMs(toast.message, hasActions, overflowPx);
  // Hovering, focusing, or dragging freezes the countdown so the toast cannot
  // vanish mid-read or mid-swipe.
  const paused = hovered || focusWithin || dragX !== 0;

  const remainingRef = useRef(totalMs);
  useEffect(() => {
    remainingRef.current = totalMs;
  }, [toast.id, totalMs]);

  useEffect(() => {
    if (paused) return undefined;
    const startedAt = Date.now();
    // Hovering past the whole dwell drains `remaining` to 0, and resuming on
    // exactly 0 makes the toast blink out on the same frame the pointer leaves.
    // Hand back a moment so it reads as leaving rather than being snatched.
    const handle = window.setTimeout(onDismiss, Math.max(RESUME_GRACE_MS, remainingRef.current));
    return () => {
      window.clearTimeout(handle);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAt));
    };
  }, [onDismiss, paused, toast.id, totalMs]);

  // Esc dismisses, matching every other transient surface in the app.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onDismiss]);

  function handleLinkClick(event: ReactMouseEvent<HTMLAnchorElement>) {
    // Let the browser own modified clicks so "open in new tab" still works.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    if (link) openEntityRoute(link);
    onDismiss();
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    // Swipe is a touch/pen affordance; a mouse drag here would fight text
    // selection, and the mouse already has the X button.
    //
    // Since #1489 these handlers only ever run where touch is the primary input:
    // everywhere else the pill's body is `pointer-events: none` and the gesture
    // never reaches it. That is the same set of devices the affordance was
    // written for, and the `mouse` guard below stays as the code's own statement
    // of intent rather than relying on the stylesheet to enforce it.
    if (event.pointerType === "mouse") return;
    if ((event.target as HTMLElement).closest("a, button")) return;
    dragRef.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, active: false };
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.active) {
      if (Math.abs(dx) < SWIPE_START_PX) return;
      // A mostly-vertical drag is the page scrolling past the toast, not a
      // dismiss; hand it back rather than hijacking the gesture.
      if (Math.abs(dy) > Math.abs(dx)) {
        dragRef.current = null;
        return;
      }
      drag.active = true;
      // Capture keeps the gesture alive if the finger leaves the pill, but it
      // throws when the pointer is no longer active (a cancelled or synthetic
      // one). Losing capture is survivable; losing the handler is not.
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // no capture — the swipe still tracks while the pointer stays on the pill
      }
    }
    setDragX(dx);
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    // A different pointer finishing (a second finger, a stray mouse) says
    // nothing about this gesture — leave the tracked drag alone.
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (drag.active && Math.abs(event.clientX - drag.startX) >= SWIPE_DISMISS_PX) {
      onDismiss();
      return;
    }
    setDragX(0);
  }

  // pointercancel is the gesture being taken away (the OS claimed it, the
  // browser started scrolling, the pen left range) — never a completed swipe.
  // Sharing the pointerup path here dismissed toasts on cancelled gestures that
  // happened to have travelled far enough.
  function handlePointerCancel(event: ReactPointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (drag && drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setDragX(0);
  }

  const marquee = overflowPx > 0 && !reduceMotion;
  const style: CSSProperties & Record<string, string | number> = {
    "--toast-drag": `${Math.round(dragX)}px`,
    "--toast-drag-opacity": String(Math.max(0.25, 1 - Math.abs(dragX) / SWIPE_FADE_PX))
  };
  if (marquee) {
    style["--toast-marquee-shift"] = `${-overflowPx}px`;
    style["--toast-marquee-duration"] = `${marqueeCycleMs(overflowPx)}ms`;
    style["--toast-marquee-delay"] = `${MARQUEE_DELAY_MS}ms`;
  }

  const classNames = ["app-toast"];
  if (link) classNames.push("has-link");
  if (marquee) classNames.push("is-marquee");
  if (dragX !== 0) classNames.push("is-dragging");
  if (paused) classNames.push("is-paused");

  return (
    <div className="app-toast-anchor">
      <div
        ref={pillRef}
        className={classNames.join(" ")}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={style}
        onFocus={() => setFocusWithin(true)}
        onBlur={() => setFocusWithin(false)}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        <CheckCircle2 className="app-toast-icon" size={18} aria-hidden="true" />
        {link ? (
          <a className="app-toast-link" href={link.path} onClick={handleLinkClick}>
            {link.label}
          </a>
        ) : null}
        {/* title carries the full line for the wrap/reduced-motion fallback and
            as a plain hover affordance while the marquee is mid-scroll. */}
        <span className="app-toast-message" ref={viewportRef} title={overflowPx > 0 || reduceMotion ? toast.message : undefined}>
          <span className="app-toast-message-text" ref={textRef}>{toast.message}</span>
        </span>
        {toast.actions?.length ? (
          <span className="app-toast-actions">
            {toast.actions.map((action) => (
              <button
                type="button"
                className="app-toast-action"
                key={action.label}
                onClick={() => { void action.onClick(); }}
              >
                {action.label}
              </button>
            ))}
          </span>
        ) : null}
        <button
          type="button"
          className="app-toast-dismiss"
          aria-label={t("toast.dismissNotification")}
          title={t("common.dismiss")}
          onClick={onDismiss}
        >
          <X size={14} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
